import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

type LeadStatus = 'lead' | 'trial' | 'active' | 'at_risk' | 'churned';

interface Lead {
  id: string;
  email: string;
  companyName: string;
  recommendedTier: string;
  recommendedRegion: string;
  frameworks: string[];
  status: LeadStatus;
  createdAt: string;
  assessmentAnswers?: Record<string, string>;
  notes?: string;
  followUpDate?: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'lead', label: 'Lead' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'churned', label: 'Churned' },
];

const LEAD_STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'churned', label: 'Churned' },
];

export function LeadPipeline() {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editFollowUp, setEditFollowUp] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = !!user?.isPlatformAdmin;

  useEffect(() => {
    if (!isAdmin) return;
    fetchLeads();
  }, [isAdmin]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      if (filterStatus && l.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !l.email.toLowerCase().includes(q) &&
          !l.companyName.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [leads, filterStatus, search]);

  async function fetchLeads() {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<Lead[]>(
      '/api/admin/leads'
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setLeads(data);
    }
    setLoading(false);
  }

  function startEditing(lead: Lead) {
    setEditingId(lead.id);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
    setEditFollowUp(lead.followUpDate || '');
  }

  function cancelEditing() {
    setEditingId(null);
    setEditStatus('');
    setEditNotes('');
    setEditFollowUp('');
  }

  async function handleSaveEdit(leadId: string) {
    setSaving(true);
    setError(null);

    const { error: apiError } = await apiClient.patch(
      `/api/admin/leads/${leadId}`,
      {
        status: editStatus,
        notes: editNotes.trim() || undefined,
        followUpDate: editFollowUp || undefined,
      }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId
            ? {
                ...l,
                status: editStatus as LeadStatus,
                notes: editNotes.trim() || undefined,
                followUpDate: editFollowUp || undefined,
              }
            : l
        )
      );
      cancelEditing();
    }
    setSaving(false);
  }

  const statusBadgeVariant = (status: LeadStatus) => {
    switch (status) {
      case 'lead':
        return 'info' as const;
      case 'trial':
        return 'brand' as const;
      case 'active':
        return 'success' as const;
      case 'at_risk':
        return 'warning' as const;
      case 'churned':
        return 'danger' as const;
      default:
        return 'default' as const;
    }
  };

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">
            Access Denied
          </h1>
          <p className="text-slate-400">
            You do not have permission to view this page. Platform admin access is required.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">
          Lead Pipeline
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {leads.length} lead{leads.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-surface-mid rounded-xl border border-border p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            placeholder="Search by email or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          />
        </div>
      </div>

      {/* Lead list */}
      {filteredLeads.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">
              {leads.length === 0
                ? 'No leads found.'
                : 'No leads match your filters.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => {
            const isExpanded = expandedId === lead.id;
            const isEditing = editingId === lead.id;

            return (
              <div
                key={lead.id}
                className="bg-surface-mid rounded-xl border border-border overflow-hidden"
              >
                {/* Lead row */}
                <div
                  className="px-6 py-4 cursor-pointer hover:bg-surface-light/20 transition-colors"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : lead.id)
                  }
                >
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                    <div className="sm:col-span-3">
                      <p className="text-sm font-medium text-white truncate">
                        {lead.email}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-sm text-slate-400 truncate">
                        {lead.companyName}
                      </p>
                    </div>
                    <div className="sm:col-span-1">
                      <Badge variant="brand">{lead.recommendedTier}</Badge>
                    </div>
                    <div className="sm:col-span-1">
                      <Badge variant="info">
                        {lead.recommendedRegion.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex flex-wrap gap-1">
                        {lead.frameworks.slice(0, 2).map((fw) => (
                          <Badge key={fw} variant="default">
                            {fw}
                          </Badge>
                        ))}
                        {lead.frameworks.length > 2 && (
                          <Badge variant="default">
                            +{lead.frameworks.length - 2}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-1">
                      <Badge variant={statusBadgeVariant(lead.status)}>
                        {lead.status === 'at_risk' ? 'At Risk' : lead.status}
                      </Badge>
                    </div>
                    <div className="sm:col-span-2 text-right">
                      <p className="text-sm text-slate-500">
                        {formatDate(lead.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-6 py-4 border-t border-border bg-surface-dark/30">
                    {/* Assessment answers */}
                    {lead.assessmentAnswers &&
                      Object.keys(lead.assessmentAnswers).length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                            Assessment Answers
                          </p>
                          <div className="space-y-2">
                            {Object.entries(lead.assessmentAnswers).map(
                              ([question, answer]) => (
                                <div key={question}>
                                  <p className="text-xs text-slate-400">
                                    {question}
                                  </p>
                                  <p className="text-sm text-white">{answer}</p>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {/* Notes */}
                    {lead.notes && !isEditing && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          Notes
                        </p>
                        <p className="text-sm text-slate-300">{lead.notes}</p>
                      </div>
                    )}

                    {/* Follow-up date */}
                    {lead.followUpDate && !isEditing && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          Follow-up Date
                        </p>
                        <p className="text-sm text-white">
                          {formatDate(lead.followUpDate)}
                        </p>
                      </div>
                    )}

                    {/* Edit form */}
                    {isEditing ? (
                      <div className="space-y-4 border-t border-border pt-4">
                        <Select
                          label="Status"
                          options={LEAD_STATUS_OPTIONS}
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                        />
                        <div className="space-y-1.5">
                          <label className="block text-sm font-medium text-slate-300">
                            Notes
                          </label>
                          <textarea
                            className="w-full px-3 py-2 bg-surface-dark border border-border rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-none"
                            rows={3}
                            placeholder="Add notes..."
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                          />
                        </div>
                        <Input
                          label="Follow-up Date"
                          type="date"
                          value={editFollowUp}
                          onChange={(e) => setEditFollowUp(e.target.value)}
                        />
                        <div className="flex items-center gap-3">
                          <Button
                            size="sm"
                            loading={saving}
                            onClick={() => handleSaveEdit(lead.id)}
                          >
                            Save Changes
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelEditing}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="pt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(lead);
                          }}
                        >
                          Edit Lead
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
